import React from "react";
import PropTypes from "prop-types";
//Material imports
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ArrowBack from "@mui/icons-material/ArrowBackIos";
// Limitless imports
import { MFA_CTX_SETUP, MFA_CTX_LOGIN, MFA_EXPIRY_CODE_PROVIDED } from "@limitless/portal";
import { useTranslation } from "@limitless/common/hooks";
import { useMFASetupContext } from "../../hooks/mfa-setup/MFASetupContext";
import OkCancelDialog from "../../components/dialogs/ok-cancel-dialog/OkCancelDialog";
const useStyles = makeStyles(theme => ({
  logOutBtn: {
    color: theme.palette.primary.main,
    paddingLeft: 0,
    marginBottom: 20
  },
  logOutText: {
    marginLeft: 10,
    fontWeight: 500,
    fontSize: "0.8rem"
  },
  arrow: {
    fontWeight: 500,
    height: 10,
    width: 10
  }
}));
let mfaVerificationFrozen;
const MfaComponentWrapper = ({
  mfaCtx,
  userName,
  header,
  subtitle1,
  subtitle2,
  subtitle3,
  children
}) => {
  const classes = useStyles();
  const {
    t
  } = useTranslation();
  const {
    verifyMFAState,
    backToLogin,
    setMfaCtx
  } = useMFASetupContext();
  const [signoutState, setSignoutState] = React.useState(false);
  React.useEffect(() => {
    setMfaCtx(mfaCtx);
  }, []);
  React.useEffect(() => {
    if (verifyMFAState.freezeVerification) {
      mfaVerificationFrozen = verifyMFAState.freezeVerification;
    }
  }, [verifyMFAState.freezeVerification]);

  /* 
   During MFA login process, AWS Cognito seems to issue a not-authorized error instead of code-expiry when submitting an mfa code after a 3 minute of idle period. 
   
   So this where it, kinda, gets fixed for relevant UX.
  */
  const reloadInfo = React.useMemo(() => {
    const item = {
      childText: "Session has timed out. Please reload the page to continue",
      title: "Session timeout",
      btnTxt: "Reload"
    };
    if (mfaCtx === MFA_CTX_LOGIN) {
      item.childText = MFA_EXPIRY_CODE_PROVIDED;
      item.title = "Code expired";
      item.btnTxt = "Retry";
    }
    return item;
  }, []);
  return /*#__PURE__*/React.createElement(Grid, null, mfaCtx === MFA_CTX_SETUP && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    className: classes.logOutBtn,
    onClick: () => setSignoutState(true),
    id: "signOutId",
    disabled: false
  }, /*#__PURE__*/React.createElement(ArrowBack, {
    className: classes.arrow
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    className: classes.logOutText
  }, t("Back to log in")))), /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h2",
    color: "textPrimary"
  }, header)), /*#__PURE__*/React.createElement(Box, {
    minHeight: 24
  }), /*#__PURE__*/React.createElement(Grid, {
    component: Typography,
    variant: "subtitle1",
    color: "textPrimary"
  }, subtitle1), subtitle2 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    minHeight: 5
  }), /*#__PURE__*/React.createElement(Grid, {
    className: classes.subtitle2,
    component: Typography,
    variant: "subtitle1",
    color: "textPrimary"
  }, subtitle2)), /*#__PURE__*/React.createElement(Box, {
    minHeight: 24
  }), /*#__PURE__*/React.createElement(Grid, {
    component: Typography,
    variant: "subtitle1",
    color: "textPrimary"
  }, subtitle3), /*#__PURE__*/React.createElement(Box, {
    minHeight: 24
  }), /*#__PURE__*/React.createElement(Grid, null, children(mfaVerificationFrozen)), /*#__PURE__*/React.createElement(OkCancelDialog, {
    key: 1,
    open: signoutState,
    title: "Sign Out",
    onCancelClick: () => setSignoutState(false),
    cancelLabel: "Cancel",
    cancelId: "Cancel",
    onOKClick: () => backToLogin(userName),
    okLabel: "Confirm",
    okId: "signOutMFA"
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "textPrimary"
  }, t("Are you sure you want to Sign Out?"))), /*#__PURE__*/React.createElement(OkCancelDialog, {
    key: 0,
    open: verifyMFAState.reload === true,
    title: t(reloadInfo.title),
    onOKClick: () => window.location.reload(),
    okLabel: t(reloadInfo.btnTxt),
    okId: "reloadButtonMFA"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "title",
    color: "textSecondary"
  }, t(reloadInfo.childText))));
};
export default MfaComponentWrapper;
MfaComponentWrapper.propTypes = {
  header: PropTypes.string.isRequired,
  mfaCtx: PropTypes.string.isRequired,
  subtitle1: PropTypes.string.isRequired,
  subtitle2: PropTypes.string,
  subtitle3: PropTypes.string,
  userName: PropTypes.string,
  children: PropTypes.func.isRequired
};